import {
  ASK_STARTERS,
  CHAPTERS,
  METRICS,
  PROTOCOLS,
  PUBLIC_SIGNALS,
  SOURCES,
} from '../data/research'

const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'do', 'does', 'for', 'from',
  'how', 'i', 'in', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'this', 'to', 'what',
  'when', 'where', 'which', 'who', 'why', 'with', 'would',
])

const tokenize = (value) => String(value)
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, ' ')
  .split(/\s+/)
  .filter((word) => word.length > 1 && !STOP_WORDS.has(word))

const scoreText = (tokens, text) => {
  const haystack = String(text).toLowerCase()
  return tokens.reduce((score, token) => score + (haystack.includes(token) ? 1 : 0), 0)
}

const metricText = (metric) => [
  metric.name,
  metric.group,
  metric.what,
  metric.tells,
  metric.check,
  metric.method,
  ...metric.inputs,
  ...metric.questions,
].join(' ')

const chapterText = (chapter) => [
  chapter.name,
  chapter.job,
  chapter.question,
  ...chapter.feeds,
  ...chapter.charts,
  ...chapter.metrics,
].join(' ')

const protocolText = (protocol) => Object.values(protocol).join(' ')

const relatedSourceKeys = (question) => {
  const q = question.toLowerCase()
  if (/scam|fraud|attack|defen|loss/.test(q)) return ['chainalysis', 'cftc']
  if (/agent|payment|settle|x402|merchant/.test(q)) return ['x402']
  if (/rule|regulat|mica|govern|compliance/.test(q)) return ['mica', 'bis', 'cftc']
  if (/market|price|token|leader|valuation|cap/.test(q)) return ['coingecko']
  if (/develop|github|code|repository/.test(q)) return ['github']
  if (/prompt|search|retail|interest/.test(q)) return ['trends']
  return ['bis', 'coingecko']
}

const specialMetricIds = (question) => {
  const q = question.toLowerCase()
  if (/160|agent|payment|settle|merchant/.test(q)) return ['agentic-settlement', 'paid-demand', 'subsidy-dependence']
  if (/hype|overvalu|valuation|token story|utility/.test(q)) return ['valuation-utility', 'value-capture', 'paid-demand']
  if (/leader|concentrat|breadth|category/.test(q)) return ['without-leader', 'valuation-utility', 'paid-demand']
  if (/trust|delegate|permission|autonom/.test(q)) return ['trust-frontier', 'delegation-ratio', 'prompt-outcome']
  if (/scam|fraud|attack|defen|loss/.test(q)) return ['threat-defense', 'trust-frontier', 'readiness-index']
  if (/subsid|reward|incentive|emission|dilution/.test(q)) return ['subsidy-dependence', 'paid-demand', 'value-capture']
  if (/institution|desk|firm|bank|fund/.test(q)) return ['readiness-index', 'delegation-ratio', 'trust-frontier']
  return []
}

const rank = (question, rows, makeText) => {
  const tokens = tokenize(question)
  return [...rows]
    .map((row) => ({ row, score: scoreText(tokens, makeText(row)) }))
    .sort((a, b) => b.score - a.score)
}

const buildLocalAnswer = (question) => {
  const preferred = specialMetricIds(question)
  const rankedMetrics = rank(question, METRICS, metricText)
  const metrics = [
    ...preferred.map((id) => METRICS.find((metric) => metric.id === id)),
    ...rankedMetrics.filter(({ score }) => score > 0).map(({ row }) => row),
  ].filter(Boolean).filter((metric, index, list) => list.findIndex((item) => item.id === metric.id) === index).slice(0, 3)

  const selectedMetrics = metrics.length ? metrics : METRICS.filter((metric) => ['paid-demand', 'trust-frontier', 'without-leader'].includes(metric.id))
  const rankedChapters = rank(question, CHAPTERS, chapterText)
  const chapter = rankedChapters[0]?.score > 0 ? rankedChapters[0].row : CHAPTERS[7]
  const rankedProtocols = rank(question, PROTOCOLS, protocolText)
  const protocol = rankedProtocols[0]?.score > 0 ? rankedProtocols[0].row : null
  const sourceKeys = relatedSourceKeys(question)
  const sourceLinks = sourceKeys.map((key) => ({ key, ...SOURCES[key] })).filter(Boolean)
  const q = question.toLowerCase()

  let finding = selectedMetrics[0].tells
  let catchText = selectedMetrics[0].check

  if (/160|agent|payment|settle|x402/.test(q)) {
    finding = 'No. It proves technical and transaction activity at scale, but not yet broad, durable demand. The decisive evidence is net external value, unique counterparties, repeat merchants and activity after retries and self-payments are removed.'
    catchText = PUBLIC_SIGNALS[0].catch
  } else if (/scam|fraud|attack|defen/.test(q)) {
    finding = 'The checked public evidence currently points to attackers gaining measurable economic leverage from AI. A fair defense comparison still needs prevented losses, detection lead time and false-positive data from platforms.'
    catchText = 'Visible scam proceeds and provider-linked attribution do not capture every AI-assisted attack, while defense performance is rarely disclosed consistently.'
  } else if (/leader|concentrat|breadth|category/.test(q)) {
    finding = 'Remove the largest constituent before calling the AI-token move broad. Then compare both market value and paid use, because a diverse token basket can still depend on one economic engine.'
    catchText = 'Category taxonomies can include wrappers, duplicated exposure and projects whose AI link is mainly narrative.'
  } else if (/hype|overvalu|valuation|token story|utility/.test(q)) {
    finding = 'Start with the Valuation-to-Utility Gap, then test Paid Demand and Value Capture. A high valuation is not the failure; the failure is a weak or unverifiable bridge from customer spending to the token.'
    catchText = 'Market value and operating use must be shown side by side. Combining them into one neat score would hide the disagreement that matters.'
  } else if (/paid demand|real demand|customer|revenue/.test(q)) {
    finding = 'Paid demand is strongest where repeat customers pay without a token reward and the same dollar reaches the protocol or tokenholder. The page marks this as a VPS calculation because public activity counts alone cannot prove it.'
    catchText = 'A protocol can publish jobs, users or transactions without revealing whether the demand side paid, returned or received incentives.'
  } else if (/trust|delegate|permission|autonom/.test(q)) {
    finding = 'Trust is not one number. The useful frontier is the highest-risk action a person allows without approval, from explanation through execution and key custody.'
    catchText = 'Rising AI use may leave financial authority almost unchanged. That outcome favors copilots, controls and alerts over fully autonomous portfolios.'
  }

  return {
    mode: 'Checked evidence only',
    title: selectedMetrics[0].name,
    finding,
    catch: catchText,
    metrics: selectedMetrics,
    chapter,
    protocol,
    sources: sourceLinks,
    next: selectedMetrics.flatMap((metric) => metric.questions).slice(0, 3),
    confidence: sourceLinks.length && specialMetricIds(question).length ? 'Evidence anchored' : 'Research direction',
  }
}

const remoteUrl = import.meta.env.VITE_PROMPTED_PORTFOLIO_ASK_URL

export async function askPortfolio(question) {
  if (!question.trim()) throw new Error('Ask a research question first.')

  if (remoteUrl) {
    try {
      const response = await fetch(remoteUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, surface: 'prompted-portfolio', evidenceMode: 'checked' }),
      })
      if (!response.ok) throw new Error(`Research service returned ${response.status}`)
      const data = await response.json()
      if (data?.finding && data?.catch) return { ...data, mode: data.mode || 'VPS evidence service' }
    } catch (error) {
      return { ...buildLocalAnswer(question), serviceNote: `VPS service unavailable. ${error.message}` }
    }
  }

  await new Promise((resolve) => window.setTimeout(resolve, 420))
  return buildLocalAnswer(question)
}

export { ASK_STARTERS }
